import { ISuppliersResult } from 'API/Suppliers/suppliers';
import { ITherapyFilters } from 'Context/SuppliersContext/TherapySuppliersContext';
import { getHaversineDistance } from 'Utils/getHaversineDistance';
import { getShortestTwoCoordinates } from './SuppliersReducer';

/**
 * Returns a filtered therapy suppliers array according to a given filler object.
 * Algorithm Example: [x,y,z,w] -> filter1: [x,y,z] -> filter2: [z,y] and etc.
 * @param data Therapy Suppliers Total Results Array
 * @param filters Filter Object Edited by the User
 */
export function TherapyFilter(
  data: ISuppliersResult[],
  filters: ITherapyFilters
) {
  const {
    supName,
    specialty,
    expertise,
    city,
    accessible,
    lng,
    gender,
    treatMethod,
    homeVisit,
    instituteId,
    orderByAscendingAlphabet,
    orderByDescendingAlphabet,
    location,
  } = filters;

  const filterByName = supName
    ? data.filter((therapyItem: ISuppliersResult) =>
        therapyItem.name.includes(supName)
      )
    : data;

  const filterBySpecialty = specialty
    ? filterByName.filter((therapyItem: ISuppliersResult) =>
        therapyItem.professions.includes(specialty)
      )
    : filterByName;

  const filterByExpertise =
    expertise?.length > 0
      ? filterBySpecialty.filter((therapyItem: ISuppliersResult) =>
          expertise.find((expertiseId: number) =>
            therapyItem.expertises.includes(expertiseId)
          )
        )
      : filterBySpecialty;

  const filterByCity =
    city && city.length > 0
      ? filterByExpertise.filter(
          (therapyItem: ISuppliersResult) =>
            (therapyItem.firstCityId &&
              city.includes(therapyItem.firstCityId)) ||
            (therapyItem.secondCityId &&
              city.includes(therapyItem.secondCityId))
        )
      : filterByExpertise;

  const filterByAccessible = accessible
    ? filterByCity.filter(
        (therapyItem: ISuppliersResult) =>
          therapyItem.hasAccessibilityForHandicapped
      )
    : filterByCity;

  const filterByLanguages =
    lng && lng.length > 0
      ? filterByAccessible.filter((therapyItem: ISuppliersResult) =>
          therapyItem.languages.some((languageId: number) =>
            lng.includes(languageId)
          )
        )
      : filterByAccessible;

  const filterByGender =
    gender && gender !== -1
      ? filterByLanguages.filter(
          (therapyItem: ISuppliersResult) => therapyItem.gender === gender
        )
      : filterByLanguages;

  const filterByTreatMethods =
    treatMethod && treatMethod.length > 0
      ? filterByGender.filter((therapyItem: ISuppliersResult) =>
          therapyItem.treatmentMethods.some((treatMethodId: number) =>
            treatMethod.includes(treatMethodId)
          )
        )
      : filterByGender;

  const filterByHomeVisit = homeVisit
    ? filterByTreatMethods.filter(
        (therapyItem: ISuppliersResult) => therapyItem.hasHomeVisit
      )
    : filterByTreatMethods;

  const filterByInstitute =
    instituteId !== null
      ? filterByHomeVisit.filter((therapyItem: ISuppliersResult) => {
          return !!therapyItem.instituteId === instituteId;
        })
      : filterByHomeVisit;

  if (orderByAscendingAlphabet)
    filterByInstitute.sort((a: ISuppliersResult, b: ISuppliersResult) =>
      a.name.localeCompare(b.name)
    );
  if (orderByDescendingAlphabet)
    filterByInstitute.sort((a: ISuppliersResult, b: ISuppliersResult) =>
      b.name.localeCompare(a.name)
    );

  const filteredByLocation = location
    ? filterByInstitute.filter(
        (therapyItem: ISuppliersResult) =>
          (therapyItem.firstLatitude && therapyItem.firstLongitude) ||
          (therapyItem.secondLatitude && therapyItem.secondLongitude)
      )
    : filterByInstitute;

  const filterByDistance = location
    ? filteredByLocation.sort((a: ISuppliersResult, b: ISuppliersResult) => {
        const { coordsA, coordsB } = getShortestTwoCoordinates(location, a, b);
        if (coordsA && coordsB)
          return (
            getHaversineDistance(location, coordsA) -
            getHaversineDistance(location, coordsB)
          );
        return 0;
      })
    : filteredByLocation;

  const filteredTherapy = [
    ...new Set([...filterByDistance, ...filterByInstitute]),
  ];

  return filteredTherapy;
}
